/**
 * Cyphex Teams — conversations, messages, reactions, threads, polls.
 * Mirrors infra/sql/004_messaging.sql.
 *
 * ONE model backs DMs, group chats, secret chats, channel posts, meeting chat
 * and call chat. Everything is a `Conversation` with a different `kind`.
 */

import type {
  AvatarColor,
  CallId,
  ChannelId,
  ConversationId,
  Cursor,
  FileId,
  IsoDateTime,
  MeetingId,
  MemberRole,
  MembershipState,
  MessageId,
  NotificationLevel,
  PollId,
  Seq,
  TeamId,
  TenantId,
  Timestamps,
  Uuid,
  UserId,
} from './common';
import type { UserRef } from './identity';
import type { FileRef } from './files';

/* ---------------------------------------------------------------------------
 * Conversations
 * ------------------------------------------------------------------------- */

export type ConversationKind = 'dm' | 'group' | 'secret' | 'channel' | 'meeting' | 'call';

export interface ConversationRef {
  id: ConversationId;
  kind: ConversationKind;
  title: string | null;
  channelId: ChannelId | null;
  meetingId: MeetingId | null;
  callId: CallId | null;
}

export interface Conversation extends ConversationRef, Timestamps {
  tenantId: TenantId;
  avatarUrl: string | null;
  avatarColor: AvatarColor | null;
  /** Highest allocated seq. Clients diff against their own high-water mark. */
  lastSeq: Seq;
  lastMessageAt: IsoDateTime | null;
  lastMessage: MessagePreview | null;
  messageCount: number;
  isSecret: boolean;
  e2eeEnabled: boolean;
  /**
   * "Notes to self" — a one-member conversation that always exists and is
   * pinned to the top of the DM list.
   */
  isSelfChat: boolean;
  /** Self-destruct timer for secret chats, in seconds. */
  ttlSeconds: number | null;
  archivedAt: IsoDateTime | null;
  /**
   * Who started it — and, for a group chat, the ONLY person who may remove
   * somebody else from it. `conversation_members.role` is written `'member'`
   * for every row including the creator's, so this column is the sole durable
   * authority a group has. Null for rooms that were created by the system.
   */
  createdBy: UserId | null;
  /** The other participants. For DMs this has exactly one entry. */
  members: ConversationMemberRef[];
  memberCount: number;
  /** The caller's own state in this conversation. */
  self: ConversationSelfState;
}

export interface ConversationMemberRef {
  user: UserRef;
  role: MemberRole;
  state: MembershipState;
  joinedAt: IsoDateTime | null;
  /** How far this member has read. Only sent when receipts are enabled. */
  lastReadSeq?: Seq;
}

export interface ConversationSelfState {
  lastReadSeq: Seq;
  lastReadAt: IsoDateTime | null;
  unreadCount: number;
  unreadMentionCount: number;
  /** Where to draw the "New messages" divider. */
  firstUnreadSeq: Seq | null;
  isPinned: boolean;
  isFavorite: boolean;
  isArchived: boolean;
  isHidden: boolean;
  /** Non-null when this conversation is in the Requests tab. */
  requestState: 'pending' | 'accepted' | 'blocked' | null;
  notificationLevel: NotificationLevel | null;
  mutedUntil: IsoDateTime | null;
  /** Server-side draft, so an unsent message follows the user across devices. */
  draft: MessageDraft | null;
  draftUpdatedAt: IsoDateTime | null;
  /**
   * True when the user explicitly re-marked this chat unread. The client must
   * NOT auto-mark-read on open while this holds — that is the whole point.
   */
  isManuallyUnread: boolean;
}

export interface MessagePreview {
  messageId: MessageId;
  seq: Seq;
  senderId: UserId | null;
  kind: MessageKind;
  /** Flattened, truncated to 180 chars. Never rich content. */
  text: string;
}

/* ---------------------------------------------------------------------------
 * Rich text
 *
 * `body` is a ProseMirror-compatible document. It is the CANONICAL content;
 * `bodyText` is a flattened projection kept in sync server-side and used for
 * search, previews, notifications and accessibility.
 * ------------------------------------------------------------------------- */

export type RichMarkType = 'bold' | 'italic' | 'underline' | 'strike' | 'code' | 'link' | 'highlight';

export interface RichMark {
  type: RichMarkType;
  attrs?: { href?: string; title?: string };
}

export type RichNodeType =
  | 'doc'
  | 'paragraph'
  | 'heading'
  | 'text'
  | 'hard_break'
  | 'bullet_list'
  | 'ordered_list'
  | 'list_item'
  | 'blockquote'
  | 'code_block'
  | 'horizontal_rule'
  | 'mention'
  | 'emoji'
  | 'link_preview'
  | 'table'
  | 'table_row'
  | 'table_cell';

export interface RichNode {
  type: RichNodeType;
  text?: string;
  marks?: RichMark[];
  attrs?: {
    level?: number;
    language?: string;
    /** mention node */
    mentionKind?: MentionKind;
    mentionId?: Uuid;
    label?: string;
    /** emoji node */
    shortcode?: string;
    src?: string;
    /**
     * Resolved destination for a `channel_ref` mention. Written by the composer
     * from `GET /v1/channels/:id/permalink` so the renderer never has to know
     * the routing scheme — a link pasted a year ago still points somewhere.
     */
    href?: string;
  };
  content?: RichNode[];
}

export interface RichDocument extends RichNode {
  type: 'doc';
  content: RichNode[];
}

/* ---------------------------------------------------------------------------
 * Messages
 * ------------------------------------------------------------------------- */

export type MessageKind =
  | 'text'
  | 'post'
  | 'announcement'
  | 'praise'
  | 'poll'
  | 'voice'
  | 'file'
  | 'system'
  | 'card'
  | 'meeting_invite';

export type MessageState = 'scheduled' | 'sent' | 'edited' | 'deleted' | 'redacted' | 'failed';

/**
 * `channel` / `here` / `all` are BROADCASTS — they notify people and are gated
 * by the channel's `mentionPermission`.
 *
 * `channel_ref` is the opposite: a `#engineering` cross-reference that renders
 * as a link to another channel and notifies nobody. Keeping them as separate
 * kinds is what stops a link from fanning out a notification to a whole team.
 */
export type MentionKind =
  | 'user'
  | 'group'
  | 'channel'
  | 'team'
  | 'here'
  | 'all'
  | 'channel_ref';

/** The kinds that notify every member of the conversation. */
export const BROADCAST_MENTION_KINDS: readonly MentionKind[] = ['channel', 'here', 'all', 'team'];

export interface Mention {
  kind: MentionKind;
  id: Uuid | null;
  label: string;
}

export interface Reaction {
  /** Unicode emoji, or `:shortcode:` for tenant custom emoji. */
  emoji: string;
  count: number;
  /** True when the caller reacted with this emoji. */
  reacted: boolean;
  /** First few reactors for the hover tooltip. */
  users?: UserRef[];
}

export interface LinkPreview {
  kind: 'link';
  url: string;
  title: string | null;
  description: string | null;
  imageUrl: string | null;
  siteName: string | null;
  faviconUrl: string | null;
}

export interface FilePreview {
  kind: 'file';
  fileId: FileId;
  name: string;
  mimeType: string;
  sizeBytes: number;
  thumbnailUrl: string | null;
}

export type Preview = LinkPreview | FilePreview;

/** Bot/app card — the DevOps Bot deployment post. */
export interface MessageCard {
  headline: string;
  /** Renders the small green check next to the headline. */
  status?: 'success' | 'warning' | 'error' | 'info';
  subtitle?: string;
  /** The 4-column key/value grid (label above value). */
  fields?: Array<{ label: string; value: string; href?: string }>;
  actions?: Array<{
    id: string;
    label: string;
    style?: 'primary' | 'outline' | 'danger';
    url?: string;
  }>;
}

export interface EmbeddedMeeting {
  meetingId: MeetingId;
  title: string;
  startsAt: IsoDateTime;
  endsAt: IsoDateTime;
  /** True when the meeting is live right now (the Join button turns solid). */
  isLive: boolean;
  joinUrl: string;
}

/** System event payloads — drives the icon + text in meeting chat and channels. */
export type SystemEvent =
  | { event: 'member.joined'; actorId: UserId; targetIds: UserId[] }
  | { event: 'member.left'; actorId: UserId }
  | { event: 'member.removed'; actorId: UserId; targetIds: UserId[] }
  | { event: 'channel.renamed'; actorId: UserId; from: string; to: string }
  | { event: 'channel.description_changed'; actorId: UserId }
  | { event: 'conversation.renamed'; actorId: UserId; from: string | null; to: string }
  | { event: 'meeting.started'; meetingId: MeetingId }
  | { event: 'meeting.ended'; meetingId: MeetingId; durationMs: number }
  | { event: 'recording.started'; actorId: UserId }
  | { event: 'recording.stopped'; actorId: UserId }
  | { event: 'call.ended'; callId: CallId; durationMs: number }
  | { event: 'call.missed'; callId: CallId }
  | { event: 'pin.added'; actorId: UserId; messageId: MessageId }
  | { event: 'file.shared'; actorId: UserId; fileId: FileId };

export interface VoiceClip {
  fileId: FileId;
  url: string;
  durationMs: number;
  /** 64 normalised amplitude buckets, 0..100. Renders the waveform. */
  waveform: number[];
}

export interface Message {
  id: MessageId;
  conversationId: ConversationId;
  /** Gapless per-conversation ordinal. THE ordering key. Never sort by time. */
  seq: Seq;
  kind: MessageKind;
  state: MessageState;
  sender: UserRef | null;
  /** Present for bot posts — the client renders the "APP" chip. */
  appId: Uuid | null;

  subject: string | null;
  body: RichDocument | null;
  bodyText: string | null;

  // Threading
  threadRootId: MessageId | null;
  replyToId: MessageId | null;
  /** Denormalised quoted message for reply rendering. */
  replyTo: MessagePreview | null;
  replyCount: number;
  lastReplyAt: IsoDateTime | null;
  /** Facepile on the post card (up to 5, overlapping -8px). */
  replyParticipants: UserRef[];

  // Forwarding
  forwardedFrom: {
    messageId: MessageId;
    conversationId: ConversationId;
    conversationTitle: string | null;
    sender: UserRef | null;
  } | null;

  // Content extras
  mentions: Mention[];
  attachments: MessageAttachment[];
  previews: Preview[];
  card: MessageCard | null;
  poll: Poll | null;
  meeting: EmbeddedMeeting | null;
  voice: VoiceClip | null;
  systemEvent: SystemEvent | null;

  // Engagement
  reactions: Reaction[];

  // Flags
  isPinned: boolean;
  isImportant: boolean;
  isUrgent: boolean;
  isBookmarked: boolean;
  /** In the caller's personal Saved queue (`cyphex.saved_messages`). */
  isSaved: boolean;
  /** Set when the caller has a pending reminder armed on this message. */
  reminderAt: IsoDateTime | null;

  editedAt: IsoDateTime | null;
  editCount: number;
  scheduledFor: IsoDateTime | null;
  expiresAt: IsoDateTime | null;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  deletedAt: IsoDateTime | null;
}

export interface MessageAttachment {
  id: Uuid;
  file: FileRef;
  sortOrder: number;
}

/* ---------------------------------------------------------------------------
 * Sending
 * ------------------------------------------------------------------------- */

export interface SendMessageRequest {
  /**
   * Client-generated UUID. REQUIRED. Doubles as the idempotency key: retrying
   * the same clientMsgId returns the original message instead of duplicating.
   */
  clientMsgId: Uuid;
  kind?: MessageKind;
  subject?: string;
  body?: RichDocument;
  /** Markdown alternative to `body`; server converts and stores both. */
  markdown?: string;
  /** Plain text shortcut for simple sends. */
  text?: string;
  threadRootId?: MessageId;
  replyToId?: MessageId;
  fileIds?: FileId[];
  mentions?: Mention[];
  /** ISO time in the future. Creates a scheduled message. */
  scheduledFor?: IsoDateTime;
  isImportant?: boolean;
  isUrgent?: boolean;
  poll?: CreatePollRequest;
  card?: MessageCard;
  voice?: { fileId: FileId; durationMs: number; waveform: number[] };
  /**
   * "I saw the data-protection warning and I mean it."
   *
   * Honoured only for a `warn` rule that is not administrator-locked. On a
   * locked rule, or on a `block`, it is ignored and the send is still refused —
   * see `DlpRefusal` in dlp.ts.
   */
  dlpAcknowledged?: boolean;
}

export interface EditMessageRequest {
  body?: RichDocument;
  markdown?: string;
  text?: string;
  subject?: string;
  mentions?: Mention[];
}

export interface ForwardMessageRequest {
  /** Target conversations. Max 20 per request. */
  conversationIds: ConversationId[];
  /** Optional note prepended to each forward. */
  comment?: string;
}

export interface MessageDraft {
  body: RichDocument | null;
  text: string | null;
  fileIds: FileId[];
  replyToId: MessageId | null;
  threadRootId: MessageId | null;
  updatedAt: IsoDateTime;
}

/* ---------------------------------------------------------------------------
 * Timeline queries
 * ------------------------------------------------------------------------- */

export interface TimelineRequest {
  /** Page backwards from here (exclusive). Standard infinite scroll. */
  before?: Cursor;
  /** Page forwards from here (exclusive). Used for catch-up. */
  after?: Cursor;
  /** Anchor on a specific message and return `limit` on each side. */
  around?: MessageId;
  limit?: number;
  /** Only thread replies of this root. */
  threadRootId?: MessageId;
  /** Include soft-deleted tombstones (moderator view). */
  includeDeleted?: boolean;
}

export interface TimelineResponse {
  messages: Message[];
  /** The conversation's current high-water mark at query time. */
  lastSeq: Seq;
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  startCursor: Cursor | null;
  endCursor: Cursor | null;
}

/* ---------------------------------------------------------------------------
 * Read state
 * ------------------------------------------------------------------------- */

export interface MarkReadRequest {
  /** Mark everything up to and including this seq as read. */
  seq: Seq;
}

/**
 * Mark a conversation unread again. `seq` is the FIRST message that should
 * count as unread — omit it to rewind to the last message, which is the
 * "mark this chat unread" affordance in the list row.
 */
export interface MarkUnreadRequest {
  seq?: Seq;
}

export interface MarkUnreadResponse {
  unreadCount: number;
  lastReadSeq: Seq;
  firstUnreadSeq: Seq | null;
  manualUnreadSeq: Seq | null;
}

export interface UnreadSummary {
  totalUnread: number;
  totalMentions: number;
  conversations: Array<{
    conversationId: ConversationId;
    unreadCount: number;
    unreadMentionCount: number;
    lastSeq: Seq;
    lastReadSeq: Seq;
  }>;
}

/* ---------------------------------------------------------------------------
 * Polls
 * ------------------------------------------------------------------------- */

export interface PollOption {
  id: Uuid;
  text: string;
  sort: number;
  voteCount: number;
  /** Voters, omitted for anonymous polls. */
  voters?: UserRef[];
}

export interface Poll {
  id: PollId;
  question: string;
  options: PollOption[];
  isMultiChoice: boolean;
  isAnonymous: boolean;
  allowAddOptions: boolean;
  status: 'draft' | 'open' | 'closed';
  closesAt: IsoDateTime | null;
  voteCount: number;
  /** Option ids the caller voted for. */
  myVotes: Uuid[];
  createdBy: UserRef;
  createdAt: IsoDateTime;
}

export interface CreatePollRequest {
  question: string;
  options: string[];
  isMultiChoice?: boolean;
  isAnonymous?: boolean;
  allowAddOptions?: boolean;
  closesAt?: IsoDateTime;
}

export interface VoteRequest {
  optionIds: Uuid[];
}

/* ---------------------------------------------------------------------------
 * Conversation creation & management
 * ------------------------------------------------------------------------- */

export interface CreateConversationRequest {
  kind: Extract<ConversationKind, 'dm' | 'group' | 'secret'>;
  /** For `dm` exactly one other user; for group/secret, 2..250. */
  memberIds: UserId[];
  title?: string;
  /** Secret chats only. */
  ttlSeconds?: number;
  /** First message sent atomically with creation. */
  firstMessage?: SendMessageRequest;
}

export interface UpdateConversationRequest {
  title?: string | null;
  avatarColor?: AvatarColor;
  isPinned?: boolean;
  isFavorite?: boolean;
  isArchived?: boolean;
  isHidden?: boolean;
  notificationLevel?: NotificationLevel | null;
  /** ISO time, or `null` to unmute. */
  mutedUntil?: IsoDateTime | null;
  ttlSeconds?: number | null;
  /** Cross-device draft. `null` CLEARS it — `undefined` leaves it alone. */
  draft?: Partial<MessageDraft> | null;
}

/** Renaming a group chat writes a `conversation.renamed` system message. */
export interface RenameConversationRequest {
  title: string | null;
}

/* ---------------------------------------------------------------------------
 * Saved messages & reminders — the personal, cross-conversation queues
 * ------------------------------------------------------------------------- */

export interface SavedMessage {
  id: Uuid;
  conversationId: ConversationId;
  /** The conversation's display title at read time, for the Saved list row. */
  conversationTitle: string | null;
  /** Hydrated live: edits, reactions and deletions are reflected. */
  message: Message;
  note: string | null;
  savedAt: IsoDateTime;
}

export interface SaveMessageRequest {
  note?: string;
}

export type ReminderState = 'pending' | 'fired' | 'cancelled';

/** The quick-pick offsets the "Remind me" menu renders, in minutes. */
export const REMINDER_PRESETS = [
  { id: '20m', label: 'In 20 minutes', minutes: 20 },
  { id: '1h', label: 'In 1 hour', minutes: 60 },
  { id: '3h', label: 'In 3 hours', minutes: 180 },
  { id: 'tomorrow', label: 'Tomorrow morning', minutes: null },
] as const;

export type ReminderPresetId = (typeof REMINDER_PRESETS)[number]['id'];

export interface MessageReminder {
  id: Uuid;
  conversationId: ConversationId;
  conversationTitle: string | null;
  message: Message;
  remindAt: IsoDateTime;
  note: string | null;
  state: ReminderState;
  createdAt: IsoDateTime;
}

export interface CreateReminderRequest {
  /** Exactly one of `remindAt` or `minutes`. */
  remindAt?: IsoDateTime;
  minutes?: number;
  note?: string;
}

export interface ConversationListFilters {
  kinds?: ConversationKind[];
  /** Chat list tabs: all | unread | favorites | requests | archived. */
  filter?: 'all' | 'unread' | 'favorites' | 'requests' | 'archived';
  query?: string;
  teamId?: TeamId;
}

export interface TypingIndicator {
  conversationId: ConversationId;
  userId: UserId;
  /** Server-side expiry; the client hides the indicator at this time. */
  expiresAt: IsoDateTime;
}

export interface MessageRevision {
  revision: number;
  body: RichDocument | null;
  bodyText: string | null;
  editedBy: UserRef | null;
  createdAt: IsoDateTime;
}
